'use client';

import clsx from 'clsx';
import {
	FC,
	MouseEvent,
	PointerEvent,
	PropsWithChildren,
	useEffect,
	useRef,
	useState
} from 'react';

import css from './style.module.scss';

interface Props extends PropsWithChildren {
	outerClassName?: string;
	innerClassName?: string;
}

const HorizontalFadeScroller: FC<Props> = ({ children, outerClassName, innerClassName }) => {
	const ref = useRef<HTMLDivElement>(null);
	const drag = useRef({ x: 0, scroll: 0, moved: false, id: -1 });
	const [fade, setFade] = useState({ left: false, right: true });

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const update = () =>
			setFade({
				left: el.scrollLeft > 10,
				right: el.scrollLeft + el.clientWidth < el.scrollWidth - 10
			});

		update();
		el.addEventListener('scroll', update);
		window.addEventListener('resize', update);

		return () => {
			el.removeEventListener('scroll', update);
			window.removeEventListener('resize', update);
		};
	}, []);

	const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
		if (e.pointerType === 'touch' || e.button !== 0) return;

		drag.current = {
			x: e.clientX,
			scroll: e.currentTarget.scrollLeft,
			moved: false,
			id: e.pointerId
		};
	};

	const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
		const d = drag.current;
		if (d.id !== e.pointerId) return;

		const dx = e.clientX - d.x;
		if (!d.moved && Math.abs(dx) < 5) return;

		if (!d.moved) {
			d.moved = true;
			e.currentTarget.setPointerCapture(e.pointerId);
		}

		e.currentTarget.scrollLeft = d.scroll - dx;
	};

	const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
		if (drag.current.id !== e.pointerId) return;
		drag.current.id = -1;
	};

	const onClickCapture = (e: MouseEvent) => {
		if (!drag.current.moved) return;
		e.preventDefault();
		e.stopPropagation();
	};

	return (
		<div
			className={clsx(css.scrollOuter, outerClassName, {
				[css.scrolledLeft]: fade.left,
				[css.scrolledRight]: !fade.right
			})}
		>
			<div
				ref={ref}
				className={clsx(
					css.scrollInner,
					innerClassName,
					(fade.left || fade.right) && css.canScroll
				)}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerUp}
				onClickCapture={onClickCapture}
			>
				{children}
			</div>
		</div>
	);
};

export default HorizontalFadeScroller;
